import { z } from "zod";

export const AutomationStatusSchema = z.enum(["disabled", "enabled", "paused"]);
export type AutomationStatus = z.infer<typeof AutomationStatusSchema>;

export const AutomationConfirmationModeSchema = z.enum(["manual", "bypass"]);
export type AutomationConfirmationMode = z.infer<typeof AutomationConfirmationModeSchema>;

export const AutomationRunStatusSchema = z.enum([
  "pending",
  "running",
  "succeeded",
  "failed",
  "cancelled",
  "skipped",
]);
export type AutomationRunStatus = z.infer<typeof AutomationRunStatusSchema>;

export const AutomationSummarySchema = z.object({
  automationId: z.string().min(1),
  agentId: z.string().min(1),
  name: z.string(),
  description: z.string().nullable(),
  status: AutomationStatusSchema,
  confirmationMode: AutomationConfirmationModeSchema,
  consecutiveFailures: z.number(),
  enabledAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AutomationSummary = z.infer<typeof AutomationSummarySchema>;

const AutomationRevisionSchema = z.object({
  id: z.string(),
  automationId: z.string(),
  seq: z.number(),
  script: z.string(),
  scriptSha256: z.string(),
  declaredTools: z.array(z.string()),
  inputsSchema: z.record(z.string(), z.unknown()).nullable(),
  budget: z
    .object({
      wallClockMs: z.number().optional(),
      memoryMb: z.number().optional(),
      maxToolCalls: z.number().optional(),
      maxResultBytes: z.number().optional(),
    })
    .nullable(),
  authorKind: z.enum(["agent", "user"]),
  authorId: z.string().nullable(),
  createdAt: z.string(),
});
export type AutomationRevision = z.infer<typeof AutomationRevisionSchema>;

export const AutomationRunSchema = z.object({
  id: z.string(),
  automationId: z.string(),
  revisionId: z.string(),
  triggerKind: z.enum(["schedule", "manual"]),
  triggeredByKind: z.enum(["schedule", "user", "agent"]),
  triggeredById: z.string().nullable(),
  status: AutomationRunStatusSchema,
  errorKind: z.string().nullable(),
  errorMessage: z.string().nullable(),
  output: z.unknown(),
  log: z.string(),
  logTruncated: z.boolean(),
  resolvedInputs: z.record(z.string(), z.unknown()).nullable(),
  toolCallsMade: z.number(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  durationMs: z.number().nullable(),
  createdAt: z.string(),
});
export type AutomationRun = z.infer<typeof AutomationRunSchema>;

const AutomationOperationSchema = z.object({
  seq: z.number(),
  toolId: z.string(),
  /** `dispatched` means the call left the Gateway and its result was never seen. */
  state: z.enum(["dispatched", "succeeded", "failed"]),
  argsPreview: z.string().nullable(),
  outcomePreview: z.string().nullable(),
  error: z.string().nullable(),
  durationMs: z.number().nullable(),
});
export type AutomationOperation = z.infer<typeof AutomationOperationSchema>;

export const AutomationListDataSchema = z.object({
  automations: z.array(AutomationSummarySchema),
});
export type AutomationListData = z.infer<typeof AutomationListDataSchema>;

/** What a write returns: the automation as the list shows it. */
export const AutomationUpdateDataSchema = AutomationSummarySchema;

export const AutomationDetailDataSchema = AutomationSummarySchema.extend({
  /** Whether this reader may enable, pause, run, pin or set the mode. */
  viewerCanEdit: z.boolean(),
  /** Whether the instance-wide bypass switch is armed. */
  bypassAvailable: z.boolean(),
  /** The schedule that fires it, or null when it runs only on demand. */
  schedule: z
    .object({
      schedule: z.union([
        z.object({ type: z.literal("cron"), expr: z.string() }),
        z.object({ type: z.literal("every"), interval: z.string() }),
        z.object({ type: z.literal("at"), time: z.string() }),
      ]),
      recurring: z.boolean(),
      timezone: z.string().optional(),
      status: z.string(),
      nextFireAt: z.number().nullable(),
    })
    .nullable(),
  activeRevision: AutomationRevisionSchema.nullable(),
  revisions: z.array(
    z.object({
      revision: z.number(),
      authoredBy: z.enum(["agent", "user"]),
      createdAt: z.string(),
    }),
  ),
});
export type AutomationDetailData = z.infer<typeof AutomationDetailDataSchema>;

export const AutomationRunsDataSchema = z.object({
  runs: z.array(AutomationRunSchema),
  /** Null on the last page, which every page is until there are enough runs. */
  nextCursor: z.string().nullable(),
});
export type AutomationRunsData = z.infer<typeof AutomationRunsDataSchema>;

export const AutomationRunDetailDataSchema = z.object({
  run: AutomationRunSchema,
  operations: z.array(AutomationOperationSchema),
});
export type AutomationRunDetailData = z.infer<typeof AutomationRunDetailDataSchema>;

export const AutomationRunStartedDataSchema = z.union([
  z.object({ run: AutomationRunSchema }),
  z.object({ skipped: z.string() }),
]);
export type AutomationRunStartedData = z.infer<typeof AutomationRunStartedDataSchema>;

export const AutomationCancelDataSchema = z.object({ cancelled: z.boolean() });
